import { supabase } from './supabase';
import { DEFAULT_TAX_PROFILE, type PartnerTaxProfile } from './llcTaxAdvanced';

export const STUDIO_SETTINGS_STORAGE_KEY = 'on45d_studio_settings';
export const STUDIO_SETTINGS_CHANGE_EVENT = 'on45d_studio_settings_change';

export type HeadingScale = 'minorThird' | 'majorThird' | 'perfectFourth' | 'goldenRatio';
export type TextSize = 'small' | 'medium' | 'large';
export type ContentMaxWidth = 'full' | '1280' | '1536' | '1920';

export type LlcPartnerDefault = {
  name: string;
  sharePct: number;
  taxProfile: PartnerTaxProfile;
};

export interface StudioSettings {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
  baseFontSize: number;
  headingScale: HeadingScale;
  darkMode: boolean;
  textSize: TextSize;
  borderRadius: number;
  sidebarWidth: number;
  headerHeight: number;
  contentMaxWidth: ContentMaxWidth;
  defaultLanguage: 'en' | 'es';
  studioName: string;
  legalName: string;
  phone: string;
  addressLine1: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  currency: string;
  taxRate: number;
  taxId: string;
  contactEmail: string;
  hourlyFloor: number;
  llcTaxReservePct: number;
  llcPartners: LlcPartnerDefault[];
  serviceAgreement: {
    en: string;
    es: string;
  };
}

const LEGACY_ORANGE = '#f97316';
export const STUDIO_TEAL = '#1b7a74';
export const BRAND_FONT = '"Red Hat Display", Inter, system-ui, sans-serif';
const LEGACY_SYSTEM_FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "IBM Plex Sans", system-ui, sans-serif';
const LEGACY_BASE_FONT_SIZE = 13;
const BRAND_BASE_FONT_SIZE = 14;

export const DEFAULT_STUDIO_SETTINGS: StudioSettings = {
  primaryColor: STUDIO_TEAL,
  secondaryColor: '#0b0f14',
  accentColor: STUDIO_TEAL,
  fontFamily: BRAND_FONT,
  baseFontSize: BRAND_BASE_FONT_SIZE,
  headingScale: 'majorThird',
  darkMode: true,
  textSize: 'medium',
  borderRadius: 12,
  sidebarWidth: 208,
  headerHeight: 52,
  contentMaxWidth: '1280',
  defaultLanguage: 'es',
  studioName: 'ON45D Studio',
  legalName: 'ON45D Studio LLC',
  phone: '+18137120063',
  addressLine1: '6402 Crystal Brook Drive',
  city: 'Tampa',
  region: 'FL',
  postalCode: '33625',
  country: 'US',
  currency: 'USD',
  taxRate: 0,
  taxId: '42-1827199',
  contactEmail: 'sales@on45d.studio',
  hourlyFloor: 75,
  llcTaxReservePct: 30,
  llcPartners: [
    {
      name: 'Socio 1',
      sharePct: 50,
      taxProfile: { ...DEFAULT_TAX_PROFILE, w2Self: 40000, w2Spouse: 35000 },
    },
    {
      name: 'Socio 2',
      sharePct: 50,
      taxProfile: { ...DEFAULT_TAX_PROFILE, w2Self: 45000, w2Spouse: 30000 },
    },
  ],
  serviceAgreement: {
    en: `This Service Agreement is between ON45D Studio LLC ("Studio") and the Client named in this document.

1. Scope. Studio will perform the services described in the Statement of Work (SOW) in this brief. Work outside that scope requires a written change order.

2. Fees. Fees are those listed in the SOW. Invoices are due as stated on each invoice. Late amounts may pause work.

3. Client duties. Client will provide timely feedback, content, and access needed to complete the work. Delays caused by Client may shift the schedule.

4. Intellectual property. Upon full payment, Client owns the final deliverables created for this project. Studio retains its pre-existing tools, libraries, and know-how, and may display the work in its portfolio unless Client objects in writing.

5. Confidentiality. Each party will keep the other's non-public information confidential.

6. Limitation of liability. Studio's total liability under this agreement is limited to the fees paid for the SOW. Studio is not liable for indirect or consequential damages.

7. Term. This agreement starts when the Client approves this brief and continues until the SOW is completed or either party ends it in writing.

This is not a substitute for legal advice. By approving, the Client confirms they have read this brief, the SOW, and these terms.`,
    es: `Este Acuerdo de Servicios se celebra entre ON45D Studio LLC ("Estudio") y el Cliente indicado en este documento.

1. Alcance. El Estudio realizará los servicios descritos en el Statement of Work (SOW) de este brief. El trabajo fuera de ese alcance requiere una orden de cambio por escrito.

2. Honorarios. Los honorarios son los listados en el SOW. Las facturas vencen según cada factura. Los importes vencidos pueden pausar el trabajo.

3. Obligaciones del Cliente. El Cliente entregará a tiempo el feedback, contenido y accesos necesarios. Los retrasos del Cliente pueden mover el calendario.

4. Propiedad intelectual. Tras el pago completo, el Cliente es dueño de los entregables finales de este proyecto. El Estudio conserva sus herramientas, librerías y know-how previos, y puede mostrar el trabajo en su portafolio salvo objeción por escrito.

5. Confidencialidad. Cada parte mantendrá confidencial la información no pública de la otra.

6. Limitación de responsabilidad. La responsabilidad total del Estudio bajo este acuerdo se limita a los honorarios pagados por el SOW. El Estudio no responde por daños indirectos o consecuentes.

7. Vigencia. Este acuerdo inicia cuando el Cliente aprueba este brief y continúa hasta completar el SOW o hasta que cualquiera de las partes lo termine por escrito.

Esto no sustituye asesoría legal. Al aprobar, el Cliente confirma que ha leído este brief, el SOW y estos términos.`,
  },
};

export const FONT_OPTIONS = [
  { value: BRAND_FONT, label: 'Red Hat Display' },
  { value: LEGACY_SYSTEM_FONT, label: 'System (SF)' },
  { value: '"IBM Plex Sans", system-ui, sans-serif', label: 'IBM Plex Sans' },
  { value: '"Source Serif 4", Georgia, serif', label: 'Source Serif' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia' },
  { value: 'ui-monospace, SFMono-Regular, monospace', label: 'Mono' },
];

export const COLOR_PRESETS = [
  { name: 'Teal', primary: STUDIO_TEAL, secondary: '#0b0f14', accent: STUDIO_TEAL },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function mergeStudioSettings(partial?: unknown): StudioSettings {
  if (!isRecord(partial)) return { ...DEFAULT_STUDIO_SETTINGS };
  return {
    ...DEFAULT_STUDIO_SETTINGS,
    ...partial,
    primaryColor: migrateLegacyAccent(String(partial.primaryColor || DEFAULT_STUDIO_SETTINGS.primaryColor)),
    secondaryColor: String(partial.secondaryColor || DEFAULT_STUDIO_SETTINGS.secondaryColor),
    accentColor: String(partial.accentColor || DEFAULT_STUDIO_SETTINGS.accentColor),
    fontFamily: migrateLegacyFont(String(partial.fontFamily || DEFAULT_STUDIO_SETTINGS.fontFamily)),
    baseFontSize: migrateLegacyFontSize(Number(partial.baseFontSize ?? DEFAULT_STUDIO_SETTINGS.baseFontSize)),
    headingScale: (partial.headingScale as HeadingScale) || DEFAULT_STUDIO_SETTINGS.headingScale,
    darkMode: partial.darkMode !== false,
    textSize: (partial.textSize as TextSize) || DEFAULT_STUDIO_SETTINGS.textSize,
    borderRadius: Number(partial.borderRadius ?? DEFAULT_STUDIO_SETTINGS.borderRadius),
    sidebarWidth: Number(partial.sidebarWidth ?? DEFAULT_STUDIO_SETTINGS.sidebarWidth),
    headerHeight: Number(partial.headerHeight ?? DEFAULT_STUDIO_SETTINGS.headerHeight),
    contentMaxWidth: (partial.contentMaxWidth as ContentMaxWidth) || DEFAULT_STUDIO_SETTINGS.contentMaxWidth,
    defaultLanguage:
      partial.defaultLanguage === 'en' || partial.defaultLanguage === 'es'
        ? partial.defaultLanguage
        : DEFAULT_STUDIO_SETTINGS.defaultLanguage,
    studioName: String(partial.studioName || DEFAULT_STUDIO_SETTINGS.studioName),
    legalName: String(partial.legalName || DEFAULT_STUDIO_SETTINGS.legalName),
    phone: String(partial.phone || DEFAULT_STUDIO_SETTINGS.phone),
    addressLine1: String(partial.addressLine1 || DEFAULT_STUDIO_SETTINGS.addressLine1),
    city: String(partial.city || DEFAULT_STUDIO_SETTINGS.city),
    region: String(partial.region || DEFAULT_STUDIO_SETTINGS.region),
    postalCode: String(partial.postalCode || DEFAULT_STUDIO_SETTINGS.postalCode),
    country: String(partial.country || DEFAULT_STUDIO_SETTINGS.country),
    currency: String(partial.currency || DEFAULT_STUDIO_SETTINGS.currency),
    taxRate: Number(partial.taxRate ?? DEFAULT_STUDIO_SETTINGS.taxRate),
    taxId: String(partial.taxId || DEFAULT_STUDIO_SETTINGS.taxId),
    contactEmail: migrateContactEmail(String(partial.contactEmail || DEFAULT_STUDIO_SETTINGS.contactEmail)),
    hourlyFloor: Number(partial.hourlyFloor ?? DEFAULT_STUDIO_SETTINGS.hourlyFloor),
    llcTaxReservePct: Number(partial.llcTaxReservePct ?? DEFAULT_STUDIO_SETTINGS.llcTaxReservePct),
    llcPartners: normalizeLlcPartners(partial.llcPartners),
    serviceAgreement: normalizeServiceAgreement(partial.serviceAgreement),
  };
}

function normalizeTaxProfile(raw: unknown): PartnerTaxProfile {
  if (!isRecord(raw)) return { ...DEFAULT_TAX_PROFILE };
  return {
    w2Self: Number(raw.w2Self ?? DEFAULT_TAX_PROFILE.w2Self),
    w2Spouse: Number(raw.w2Spouse ?? DEFAULT_TAX_PROFILE.w2Spouse),
    dependents: Number(raw.dependents ?? DEFAULT_TAX_PROFILE.dependents),
    extraAnnualCredits: Number(raw.extraAnnualCredits ?? DEFAULT_TAX_PROFILE.extraAnnualCredits),
  };
}

function normalizeServiceAgreement(raw: unknown): StudioSettings['serviceAgreement'] {
  const nested = isRecord(raw) ? raw : {};
  return {
    en:
      typeof nested.en === 'string'
        ? nested.en
        : DEFAULT_STUDIO_SETTINGS.serviceAgreement.en,
    es:
      typeof nested.es === 'string'
        ? nested.es
        : DEFAULT_STUDIO_SETTINGS.serviceAgreement.es,
  };
}

export function serviceAgreementForLocale(
  settings: Pick<StudioSettings, 'serviceAgreement'>,
  locale: 'en' | 'es'
) {
  const text = settings.serviceAgreement?.[locale] || settings.serviceAgreement?.en || '';
  return text.trim();
}

export function normalizeLlcPartners(raw: unknown): LlcPartnerDefault[] {
  if (!Array.isArray(raw) || !raw.length) {
    return DEFAULT_STUDIO_SETTINGS.llcPartners.map((partner) => ({
      ...partner,
      taxProfile: { ...partner.taxProfile },
    }));
  }
  return raw.map((item, index) => {
    const row = isRecord(item) ? item : {};
    const fallback = DEFAULT_STUDIO_SETTINGS.llcPartners[index] ?? DEFAULT_STUDIO_SETTINGS.llcPartners[0];
    return {
      name: String(row.name || fallback.name || `Socio ${index + 1}`),
      sharePct: Number(row.sharePct ?? fallback.sharePct ?? 0),
      taxProfile: normalizeTaxProfile(row.taxProfile),
    };
  });
}

export function readLocalStudioSettings(): StudioSettings {
  if (typeof window === 'undefined') return { ...DEFAULT_STUDIO_SETTINGS };
  try {
    const raw = localStorage.getItem(STUDIO_SETTINGS_STORAGE_KEY);
    return mergeStudioSettings(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_STUDIO_SETTINGS };
  }
}

export function writeLocalStudioSettings(settings: StudioSettings) {
  localStorage.setItem(STUDIO_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  window.dispatchEvent(new CustomEvent(STUDIO_SETTINGS_CHANGE_EVENT, { detail: settings }));
}

function migrateLegacyAccent(color: string) {
  return color.toLowerCase() === LEGACY_ORANGE ? STUDIO_TEAL : color;
}

// The old system-font defaults were saved with every settings write; move them to the shared brand type.
function migrateLegacyFont(font: string) {
  return font === LEGACY_SYSTEM_FONT ? BRAND_FONT : font;
}

function migrateLegacyFontSize(size: number) {
  return size === LEGACY_BASE_FONT_SIZE ? BRAND_BASE_FONT_SIZE : size;
}

function migrateContactEmail(email: string) {
  return email.toLowerCase() === 'hello@on45d.studio' ? DEFAULT_STUDIO_SETTINGS.contactEmail : email;
}

export function applyStudioSettingsToDocument(settings: StudioSettings) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // The admin accent is the shared brand token from index.css; primaryColor only brands client documents.
  root.style.setProperty('--app-font', settings.fontFamily);
  root.style.setProperty('--app-font-size', `${settings.baseFontSize}px`);
  root.style.setProperty('--app-radius', `${settings.borderRadius}px`);
  root.style.setProperty('--app-sidebar-width', `${settings.sidebarWidth}px`);
  root.style.setProperty('--app-header-height', `${settings.headerHeight}px`);
  root.style.fontFamily = settings.fontFamily;
  root.style.fontSize =
    settings.textSize === 'small' ? '13px' : settings.textSize === 'large' ? '16px' : `${settings.baseFontSize}px`;
}

export async function loadStudioSettings(): Promise<StudioSettings> {
  const local = readLocalStudioSettings();
  try {
    const { data, error } = await supabase.from('studio_settings').select('settings').eq('id', 1).maybeSingle();
    if (error || !data) return local;
    const merged = mergeStudioSettings(data.settings);
    writeLocalStudioSettings(merged);
    return merged;
  } catch {
    return local;
  }
}

export async function saveStudioSettings(settings: StudioSettings): Promise<StudioSettings> {
  const merged = mergeStudioSettings(settings);
  writeLocalStudioSettings(merged);
  applyStudioSettingsToDocument(merged);
  try {
    const { error } = await supabase.from('studio_settings').upsert({
      id: 1,
      settings: merged,
      updated_at: new Date().toISOString(),
    });
    if (error) console.warn('Studio settings saved locally; remote sync failed:', error.message);
  } catch (err) {
    console.warn('Studio settings saved locally; remote sync failed:', err);
  }
  return merged;
}
